import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { GlobalSearchService } from '../../../../services';
import { GlobalSearchResponseModel, GlobalSearchResult } from '../../../../core/models';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-global-search-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './global-search-list.component.html',
  styleUrl: './global-search-list.component.scss',
})
export class GlobalSearchListComponent implements OnInit, OnDestroy {
  private globalSearchService = inject(GlobalSearchService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroy$ = new Subject<void>();

  searchQuery: string = '';
  isLoading: boolean = false;
  searchResults: GlobalSearchResponseModel | null = null;
  currentPage: number = 1;
  pageSize: number = 20;

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.searchQuery = params['q'] || '';
      this.currentPage = params['page'] ? parseInt(params['page']) : 1;
      if (this.searchQuery.trim().length >= 2) {
        this.loadSearchResults();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSearchResults(): void {
    if (!this.searchQuery.trim() || this.searchQuery.trim().length < 2) {
      return;
    }

    this.isLoading = true;
    this.globalSearchService
      .searchWithDefaults(this.searchQuery.trim(), this.currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.searchResults = results;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading search results:', error);
          this.searchResults = null;
          this.isLoading = false;
        },
      });
  }

  onResultClick(result: GlobalSearchResult): void {
    if (result.type === 'Customer') {
      this.router.navigate(['/customers/edit', result.id]);
      return;
    }

    if (result.type === 'Quote') {
      const phone = this.getPhoneFromResult(result);

      if (phone) {
        this.router.navigate(['/quotes/summary/phone', phone, result.id]);
      } else {
        this.router.navigate(['/quotes/summary', result.id]);
      }
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.searchQuery, page: this.currentPage },
      queryParamsHandling: 'merge',
    });
    this.loadSearchResults();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getHighlightedTitle(result: GlobalSearchResult): string {
    const titleField = result.highlightedFields.find((field) => field.fieldName === 'Name' || field.fieldName === 'Customer' || field.fieldName === 'Quote ID');

    return titleField ? titleField.highlightedValue : result.title;
  }

  getDisplayFields(result: GlobalSearchResult) {
    return result.highlightedFields;
  }

  getPageNumbers(): number[] {
    if (!this.searchResults) {
      return [];
    }

    const totalPages = this.searchResults.totalPages;
    const current = this.currentPage;
    const pages: number[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (current <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(totalPages);
      } else if (current >= totalPages - 2) {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = current - 1; i <= current + 1; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(totalPages);
      }
    }

    return pages;
  }

  hasPreviousPage(): boolean {
    return this.searchResults ? this.currentPage > 1 : false;
  }

  hasNextPage(): boolean {
    return this.searchResults ? this.currentPage < this.searchResults.totalPages : false;
  }

  private getPhoneFromResult(result: GlobalSearchResult): string | null {
    const phoneField = result.highlightedFields.find((field) => field.fieldName === 'Phone');
    if (!phoneField) {
      return null;
    }

    const raw = phoneField.highlightedValue ?? phoneField.value ?? '';
    const withoutHtml = raw.replace(/<[^>]+>/g, '');
    const digitsOnly = withoutHtml.replace(/\D/g, '');

    return digitsOnly || null;
  }
}
